// Day and time chips from GET /api/intake/kickoff/slots, shown in the browser's time zone.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { KickoffSlots, KickoffView } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { firstName, fmtDow, fmtMonthDay, fmtTime, localDateKey, zoneName } from '../lib/format';
import { Banner, Button, ChoiceChip, Spinner, cx } from '../ui';

type Day = { key: string; first: string; slots: Array<{ startsAt: string; endsAt: string }> };

export function KickoffPicker({ leadName, onConfirm, onContactMe, onHost, confirmVerb = 'Confirm' }: {
  leadName: string;
  onConfirm: (startsAt: string) => Promise<KickoffView>;
  onContactMe?: () => Promise<void>;
  onHost?: (host: KickoffSlots['host']) => void;
  confirmVerb?: string;
}) {
  const [data, setData] = useState<KickoffSlots | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const d = await api.get<KickoffSlots>('/api/intake/kickoff/slots');
      setData(d);
      onHost?.(d.host);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'calendar_unavailable') setUnavailable(true);
      else setLoadError('We couldn’t load available times.');
    }
  }, [onHost]);

  useEffect(() => { void load(); }, [load]);

  const days: Day[] = useMemo(() => {
    const map = new Map<string, Day>();
    for (const s of (data?.days ?? []).flatMap((d) => d.slots)) {
      const key = localDateKey(s.startsAt);
      const day = map.get(key) ?? { key, first: s.startsAt, slots: [] };
      day.slots.push(s);
      map.set(key, day);
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key)).map((d) => ({ ...d, slots: d.slots.sort((a, b) => a.startsAt.localeCompare(b.startsAt)) }));
  }, [data]);

  const day = days.find((d) => d.key === dayKey) ?? null;
  const zone = zoneName(day ? new Date(day.first) : undefined);
  const lead = firstName(leadName, 'We');

  const confirm = async () => {
    if (!startsAt) return;
    setBusy(true);
    setMessage(null);
    try {
      await onConfirm(startsAt);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'slot_taken') {
        setMessage('That time was just taken. Pick another time.');
        setStartsAt(null);
        await load();
      } else if (e instanceof ApiError && e.code === 'calendar_unavailable') {
        setUnavailable(true);
      } else {
        setMessage(e instanceof ApiError && e.message ? e.message : 'We couldn’t book that time. Try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const contactMe = async () => {
    if (!onContactMe) return;
    setBusy(true);
    setMessage(null);
    // A refusal says why (e.g. a session is already booked); only a network or server failure asks to try again.
    try { await onContactMe(); } catch (e) {
      setMessage(e instanceof ApiError && !e.retryable && e.message ? e.message : 'We couldn’t send that. Try again.');
    } finally { setBusy(false); }
  };

  const contactButton = onContactMe && (
    <Button variant="link" className="!px-2" onClick={() => void contactMe()} disabled={busy}>None of these work. Contact me instead.</Button>
  );

  if (unavailable || (data && days.length === 0)) {
    return (
      <div className="flex flex-col gap-4">
        <Banner tone="warn">
          {unavailable ? 'We can’t show available times right now.' : 'There are no open times in the next two weeks.'}
          {onContactMe ? ` Choose the option below and ${lead} will reach out within one business day to find a time that works.` : ` ${lead} will reach out to find a time that works.`}
        </Banner>
        {message && <Banner tone="error" role="alert">{message}</Banner>}
        <div>{contactButton}</div>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="flex flex-col gap-3">
        <Banner tone="error" role="alert">{loadError}</Banner>
        <div className="flex flex-wrap gap-3"><Button onClick={() => void load()}>Try again</Button>{contactButton}</div>
      </div>
    );
  }
  if (!data) return <Spinner label="Loading available times" />;

  const shortLabel = startsAt && day ? `${fmtDow(startsAt)} ${fmtMonthDay(startsAt)}, ${fmtTime(startsAt)}` : '';
  return (
    <div className="flex flex-col gap-6">
      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2.5 p-0 text-[15px] font-semibold">Pick a day</legend>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2">
          {days.map((d) => {
            const on = d.key === dayKey;
            return (
              <button
                key={d.key}
                type="button"
                aria-pressed={on}
                onClick={() => { setDayKey(d.key); setStartsAt(null); }}
                className={cx('flex min-h-16 flex-col items-center justify-center rounded-xl border p-2', on ? 'border-brand bg-brand text-white' : 'border-line-btn bg-white text-ink hover:border-brand hover:bg-brand-tint')}
              >
                <span className={cx('text-xs font-semibold uppercase tracking-[0.04em]', on ? 'text-white/80' : 'text-muted')}>{fmtDow(d.first)}</span>
                <span className="text-base font-bold">{fmtMonthDay(d.first)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2.5 p-0 text-[15px] font-semibold">Pick a time <span className="font-normal text-muted">({zone})</span></legend>
        {day ? (
          <div className="flex flex-wrap gap-2">
            {day.slots.map((s) => (
              <ChoiceChip key={s.startsAt} pressed={startsAt === s.startsAt} onClick={() => setStartsAt(s.startsAt)}>{fmtTime(s.startsAt)}</ChoiceChip>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted">Pick a day to see open times.</div>
        )}
      </fieldset>
      {message && <Banner tone="warn" role="alert">{message}</Banner>}
      <div className="flex flex-wrap items-center gap-3 border-t border-line-soft pt-5">
        {startsAt ? (
          <Button variant="primary" size="lg" onClick={() => void confirm()} disabled={busy}>{confirmVerb} {shortLabel}</Button>
        ) : (
          <Button variant="primary" size="lg" disabled>Pick a day and time</Button>
        )}
        {contactButton}
      </div>
    </div>
  );
}
